import { z } from 'zod'
import SessionStorageManager from 'services/SessionStorageManager'
import { logger } from 'services/logger'
import {
  EXTERNAL_BOOK_SOURCES,
  type BookArrival,
} from 'stores/photoBooksStore/PhotoBooksStore'
import { isClient } from 'utils/runtimeUtils'

// Session storage, not local: the notice belongs to this tab's restore, and a
// tab that closes before it shows has nothing left to say. Surviving a reload
// is the whole point — the embedded login reloads the document between the
// merge that records the arrival and the moment the user could read about it.
const PENDING_BOOK_ARRIVAL_KEY = 'pendingBookArrival'

const BookArrivalSchema = z.object({
  source: z.enum(EXTERNAL_BOOK_SOURCES),
  addedBookIds: z.array(z.string()).nonempty(),
})

export function readPendingArrival(): BookArrival | null {
  if (!isClient()) return null

  const storedArrival = SessionStorageManager.get(PENDING_BOOK_ARRIVAL_KEY)
  if (!storedArrival) return null

  const result = BookArrivalSchema.safeParse(storedArrival)
  if (!result.success) {
    logger.warning('Failed to parse the stored book arrival', {
      error: result.error,
    })
    clearPendingArrival()
    return null
  }

  return result.data
}

// One restore is one notice, so books arriving under the same source
// accumulate; a different source replaces what is stored, matching how the
// store itself records arrivals — the notice reports a single source to
// analytics, and one source cannot speak for the other's books.
export function appendPendingArrival(arrival: BookArrival): BookArrival {
  const storedArrival = readPendingArrival()
  const mergedArrival =
    storedArrival?.source === arrival.source
      ? {
          source: arrival.source,
          addedBookIds: [
            ...new Set([
              ...storedArrival.addedBookIds,
              ...arrival.addedBookIds,
            ]),
          ],
        }
      : arrival

  SessionStorageManager.set(PENDING_BOOK_ARRIVAL_KEY, mergedArrival)
  return mergedArrival
}

export function clearPendingArrival(): void {
  SessionStorageManager.remove(PENDING_BOOK_ARRIVAL_KEY)
}
